import { HttpStatusCode } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Action } from "@ngrx/store";
import { catchError, map, Observable, of, switchMap } from "rxjs";

import { FeatureType, FilterService, MetadataService } from "../generated";
import { SharedActions } from "./shared.actions";

@Injectable()
export class SharedEffects {
  private readonly filterService = inject(FilterService);
  private readonly metadataService = inject(MetadataService);
  private readonly _router = inject(Router);

  private readonly actions$: Actions = inject(Actions);

  getAllFilterProfiles$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.getAllFilterProfiles),
      switchMap((action) =>
        this.filterService
          .apiFilterProfilesGet(action.filterType)
          .pipe(
            map((filterProfilesPayload: any) =>
              SharedActions.getAllFilterProfilesSuccess({
                filterProfiles: filterProfilesPayload,
              }),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                SharedActions.getAllFilterProfilesError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));

  createProfile$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.postCreateProfile),
      switchMap((payload) =>
        this.filterService
          .apiFilterProfilesPost(payload.filterModel)
          .pipe(
            map(() =>
              SharedActions.postCreateProfileSuccess(),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                SharedActions.postCreateProfileError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));

  updateProfile$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.putUpdateFilter),
      switchMap((payload) =>
        this.filterService
          .apiFilterProfilesIdPut(payload.id, payload.filterModel)
          .pipe(
            map(() =>
              SharedActions.putUpdateFilterSuccess(),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                SharedActions.putUpdateFilterError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));

  deleteProfile$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.deleteFilter),
      switchMap((payload) =>
        this.filterService
          .apiFilterProfilesIdDelete(payload.id)
          .pipe(
            map(() =>
              SharedActions.deleteFilterSuccess(),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                SharedActions.deleteFilterError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));

  getSourceTypes$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.getSourceTypes),
      switchMap(() =>
        this.metadataService
          .apiMetadataSourceTypesGet()
          .pipe(
            map((sourceTypesPayload: any) =>
              SharedActions.getSourceTypesSuccess({
                sourceTypes: sourceTypesPayload
              }),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                SharedActions.getSourceTypesError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));

  getFeatureTypes$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.getFeatureTypes),
      switchMap(() =>
        this.metadataService
          .apiMetadataFeatureTypesGet()
          .pipe(
            map((featureTypesPayload: FeatureType[]) =>
              SharedActions.getFeatureTypesSuccess({
                featureTypes: featureTypesPayload
              }),
            ),
            catchError((err) => {
              if (err.status === HttpStatusCode.NotFound) {
                this._router.navigate(['special']);
              }
              return of(
                SharedActions.getFeatureTypesError({
                  payload: err.error,
                }),
              );
            }),
          ),
      ),
    ));
}
